import clsx from "clsx";
import { useTranslation } from "react-i18next";

import classes from "./styles.module.scss";

import { Button } from "@components/atoms/button";
import { visitHelper } from "@components/organisms/videoVisit";
import { path } from "@/routes/path";
import { useUserStore } from "@/stores/userStore";
import { useAppointmentHelper } from "@components/organisms/appointmentCard/components/appointmentHelper";
import type { IAppointmentCardButtonProps } from "@components/organisms/appointmentCard/components/appointmentCardButtons/types";
import { AppointmentTimer } from "@components/organisms/appointmentCard/components/appointmentTimer";

/**
 * AppointmentCardButtons component displays buttons for an appointment card.
 *
 * @returns AppointmentCardButtons The rendered AppointmentCardButtons component.
 */
export function AppointmentCardButtons({
  canCancel = true,
  canJoin = true,
  canWriteSummaryToAppointment,
  canViewSummary,
  canViewDetail,
  canViewRemainingTime,
  canDecline,
  canAccept,
  startDate,
  id,
  canNoShow,
  canWriteComment,
  cancelDisabled,
  appointmentType,
  summaryNoteId,
  doctorName,
  canViewIntakeForm,
  canEditIntakeForm,
  onEditIntakeForm,
}: IAppointmentCardButtonProps) {
  const { t } = useTranslation("common");

  const role = useUserStore((state) => state.role);

  const { appointmentHelper } = useAppointmentHelper(id);

  return (
    <div className={clsx(classes.appointmentButtons)}>
      {canViewRemainingTime && <AppointmentTimer startDate={startDate} />}
      {/* Button to cancel the appointment */}
      {canCancel && (
        <Button
          mode={"danger-main"}
          label={t("cancel_visit")}
          onClick={() => appointmentHelper.cancel()}
          disabled={cancelDisabled}
          className={clsx(classes.appointmentButton)}
        />
      )}
      {canDecline && (
        <Button
          mode={"danger-main"}
          label={t("decline_visit")}
          onClick={() => appointmentHelper.decline()}
          className={clsx(classes.appointmentButton)}
        />
      )}
      {/* Button to join a video call */}
      {canAccept && (
        <Button
          mode={"main"}
          label={t("accept_visit")}
          onClick={() => appointmentHelper.accept()}
          className={clsx(classes.appointmentButton)}
        />
      )}
      {canNoShow && (
        <Button
          mode={"main"}
          label={t("no_show_submit")}
          onClick={() => appointmentHelper.noShow()}
          className={clsx(classes.appointmentButton)}
        />
      )}
      {canJoin && (
        <Button
          mode={"main"}
          label={t("join_video_visit")}
          onClick={() => visitHelper.open(id, role!)}
          className={clsx(classes.appointmentButton)}
        />
      )}
      {canViewDetail && (
        <Button
          mode={"main"}
          label={t("view_detail")}
          linkTo={path.appointment.view.link(id?.toString(), appointmentType)}
          className={clsx(classes.appointmentButton)}
        />
      )}
      {canViewSummary && (
        <Button
          mode={"main"}
          label={t("view_summary_note")}
          linkTo={path.summaryNote.viewSummary.link(summaryNoteId)}
          className={clsx(classes.appointmentButton)}
        />
      )}
      {canWriteSummaryToAppointment && (
        <Button
          mode={"main"}
          label={t("write_summary_note")}
          linkTo={path.summaryNote.writeSummary.link(id)}
          className={clsx(classes.appointmentButton)}
        />
      )}
      {canWriteComment && (
        <Button
          mode={"main"}
          label={t("write_comment")}
          onClick={() => appointmentHelper.comment(startDate || "", doctorName)}
          className={clsx(classes.appointmentButton)}
        />
      )}
      {canViewIntakeForm && (
        <Button
          mode={"main"}
          label={
            canEditIntakeForm ? t("edit_intake_form") : t("view_intake_form")
          }
          onClick={onEditIntakeForm}
        />
      )}
    </div>
  );
}
